/**
 * Module for show desktop notifications
 * - will invoke web notifications if running from a browser or wrapper via localhost:61337
 */

import globalEvents from './GlobalEvents'
import _i18n from '@/utilities/I18n'
import logger from '@/utilities/Logger'
import { UAParser } from 'ua-parser-js'
import { useSettingsStore } from '@/stores/Settings'
import { useElectronStore } from '@/stores/Electron'

const browserDetect = UAParser()
const i18n = _i18n.global
let ss: ReturnType<typeof useSettingsStore>

/**
 * Notification handler
 */
class DesktopNotification {
	title: string

	options: NotificationOptions

	onClick: (() => void) | undefined

	onClose: (() => void) | undefined

	notification: Notification | undefined

	/**
	 * Show a notification
	 * @param title Title
	 * @param message Optionel message
	 * @param icon Optional url of image to show
	 * @param requireInteraction True to require user to click it
	 * @param onClick Optional callback when notification is clicked
	 * @param onClose Optional callback when notification is closed
	 */
	static show(title: string, message?: string, icon?: string, requireInteraction?: boolean, onClick?: () => void, onClose?: () => void) {
		ss = useSettingsStore()
		const instance = new DesktopNotification(title, message, icon, requireInteraction, onClick, onClose)

		return instance
	}

	/**
	 * Show a notification
	 * @param title Title
	 * @param message Optionel message
	 * @param icon Optional url of image to show
	 * @param requireInteraction True to require user to click it
	 * @param onClick Optional callback when notification is clicked
	 * @param onClose Optional callback when notification is closed
	 */
	private constructor(title: string, message?: string, icon?: string, requireInteraction?: boolean, onClick?: () => void, onClose?: () => void) {
		this.title = title
		this.onClick = onClick
		this.onClose = onClose
		this.options = {}
		if (requireInteraction) {
			this.options.silent = true // phone is ringing - does not need bell
		}
		if (message) { this.options.body = message }
		if (icon) { this.options.icon = icon }
		if (requireInteraction) { this.options.requireInteraction = requireInteraction }
		this.options.tag = 'Notify' + Math.random().toString().substring(2)

		if (ss.runtime.isEmbedded) { this.showSystemNotification() } else { this.showWebNotification() } // NOSONAR
	}

	/**
	 * Show notification using the browser's built-in notification system
	 */
	private async showWebNotification() {
		try {
			logger.debug(`MyNotification: Show web notification: ${this.title} - ${this.options.body}`)
			const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

			if (isSafari) {
				const permission: NotificationPermission = await new Promise(resolve => {
					const permission = Notification.requestPermission()

					resolve(permission)
				})
				if (permission != 'granted') { return }
			} else {
				const permission = await Notification.requestPermission()
				if (permission != 'granted') { return }
			}

			this.notification = new Notification(this.title, this.options)
			const this2 = this // eslint-disable-line @typescript-eslint/no-this-alias

			if (this.onClick) {
				this.notification.onclick = () => {
					if (this2?.onClick) {
						this2.notification?.close()
						this2.onClose = undefined
						this2.onClick()
					}
				}
			}

			if (this.onClose) {
				this.notification.onclose = () => {
					if (this2?.onClose) {
						this2.onClose()
					}
				}
			}
		} catch (err) {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('mynotification.errorHeader'), message: i18n.t('mynotification.errorMessage') + '<br><br>' + (err as Error).message })
		}
	}

	/**
	 * Show notification using the system notification system
	 */
	private async showSystemNotification() {
		if (ss.runtime.isEmbedded && browserDetect.os.name === 'MacOS') {
			this.showWebNotification()
			return
		}

		logger.debug(`DesktopNotification: Show native notification: ${this.title} - ${this.options.body}`)

		const electron = useElectronStore()
		const res = await electron.notificationShow(this.title, this.options.body, this.options.icon, this.options.tag as string)

		if (!res?.success) {
			logger.debug('DesktopNotification: Show system notification failed')
			return
		}

		if (this.onClick && res.clicked) {
			this.onClick()
		} else if (this.onClose && res.closed) {
			this.onClose()
		}
	}

	/**
	 * Close the notification - if it is still showing
	 */
	close() {
		if (ss.runtime.isEmbedded) {
			if (browserDetect.os.name === 'MacOS') {
				this.closeWebNotification()
			} else {
				this.closeSystemNotification()
			}
		} else {
			this.closeWebNotification()
		}
	}

	private closeSystemNotification() {
		const electron = useElectronStore()

		electron.notificationClose(this.options.tag as string)
	}

	private closeWebNotification() {
		if (this.notification) { this.notification.close() }
		delete this.notification
	}
}

export default DesktopNotification
